import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BASE_URL } from "./Constants";
import AddJobModal from "./components/modals/AddJobModal";
import api from "./components/login/interceptors";
import { useTheme, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CandidatesPage from "./CandidatesPage";
import ResponsiveActions from "./components/header/JdCandActionButton";
import SkillChipsPopover from "./components/table/SkillChipPopover";
import { XMarkIcon } from "@heroicons/react/20/solid";
import useFocusTrap from "./hooks/useFocusTrap";
import { ErrorToast } from "./components/modals/ErrorToast";
import ScheduleInterviewModal from "./ScheduleInterviewPage";

const JobDetailPage = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [jobUUID, setJobUUID] = useState("")
  const navigate = useNavigate();
  const [isMarkingArchived, setIsMarkingArchived] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [atsName, setAtsName] = useState();
  const [atsId, setAtsId] = useState()
  const [jobDescription, setJobDescription] = useState();
  const [jobTitle, setJobTitle] = useState();
  const [jobExperience, setJobExperience] = useState();
  const [skills, setSkills] = useState([]);
  const [preferredSkills, setPreferredSkills] = useState([]);
  const [mandatorySkills, setMandatorySkills] = useState([]);
  const [aliasSkills, setAliasSkills] = useState({})
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const theme = useTheme();

  const [isJdModalOpen, setIsJdModalOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [scheduleVersion, setScheduleVersion] = useState(0);

  const toggleModal = () => {
    setIsJdModalOpen(!isJdModalOpen);
  };

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await api.get(`${BASE_URL}/api/job-desc/${jobId}`);
        if (response.data) {
          const jobDescription = response.data ? response.data[0] : [];
          const roundedJobMinExp = Math.round(parseFloat(jobDescription.jd_min_exp));
          jobDescription.jd_min_exp = roundedJobMinExp
          setIsArchived(jobDescription.status === "archived");
          setJob(jobDescription || "");
          setJobTitle(jobDescription.title || "");
          setAtsName(jobDescription.provider || "");
          setAtsId(jobDescription.ats_ref_id)
          setJobDescription(jobDescription.job_description || "");
          setJobExperience(Math.round(parseFloat(jobDescription.jd_min_exp || 0)));
          setPreferredSkills(jobDescription.preferred_skills || []);
          setMandatorySkills(jobDescription.mandatory_skills || []);
          setAliasSkills(jobDescription.skill_aliases || {});
          setSkills(jobDescription.parsed_skills || []);
          setJobUUID(jobDescription.jd_uuid)
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobDetails();
    refreshZohoToken();
  }, [jobId]);

  const handleMarkAsArchived = async () => {
    setIsMarkingArchived(true);

    try {
      const newStatus = isArchived ? "active" : "archived";

      const response = await api.post(`${BASE_URL}/api/job-descriptions/`, {
        jd_uuid: jobId,
        status: newStatus
      });
      if (response.status === 200) {
        setIsArchived(newStatus === "archived");
      }
    } catch (error) {
      console.error("Error toggling job description status:", error);
    } finally {
      setIsMarkingArchived(false);
    }
  };

  const refreshZohoToken = async () => {
    try {
      const response = await api.post(`${BASE_URL}/api/zoho/refresh-token`);
      const newToken = response.data.access_token;

      localStorage.setItem("zoho_access_token", newToken);
      return newToken;
    } catch (error) {
      console.error("Error refreshing Zoho token:", error);
    }
  };

  const getJobOpeningsFromZoho = async (atsRefId) => {
    try {
      const response = await api.post(
        `${BASE_URL}/api/zoho/job_openings`,
        { zoho_token: localStorage.getItem("zoho_access_token") }
      );
      const jobOpenings = response.data.data;
      const matchedJob = jobOpenings.find(
        (opening) => opening.ats_ref_id === atsRefId
      );

      if (matchedJob) {
        setJobDescription(matchedJob.job_description || "");
      } else {
        console.error("Job with ats_ref_id not found in Zoho");
      }
    } catch (error) {
      throw error;
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const atsRefId = job?.ats_ref_id;
    if (!atsRefId) {
      setIsRefreshing(false);
      console.error("ats_ref_id not found");
      return;
    }

    let zohoAccessToken = localStorage.getItem("zoho_access_token");
    if (!zohoAccessToken) {
      console.error("Access token is not available.");
      setIsRefreshing(false);
      return;
    }

    try {
      await getJobOpeningsFromZoho(atsRefId);
    } catch (error) {
      if (error.response && error.response.status === 401 && error.response.data.code === "INVALID_TOKEN") {
        try {
          await refreshZohoToken();
          await getJobOpeningsFromZoho(atsRefId);
        } catch (refreshError) {
          console.error("Error refreshing token and retrying request:", refreshError);
          setError("Unable to refresh the job!");
          setIsErrorModalOpen(true);
          setTimeout(() => {
            setIsErrorModalOpen(false);
          }, 5000);
        }
      } else {
        console.error("Error fetching data from Zoho API:", error);
        setError("Unable to refresh the job!");
        setIsErrorModalOpen(true);
        setTimeout(() => {
          setIsErrorModalOpen(false);
        }, 5000);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  function handleModalOpen() {
    setIsModalOpen(true)
  }

  const viewJdRef = useFocusTrap(isJdModalOpen, () => setIsJdModalOpen(false))

  return (
    <div className="flex h-screen w-full">
      <div className="w-full always-not-show-scrollbar-horizontal" style={{ backgroundColor: theme.palette.primary.v2 }}>
        <ErrorToast message={error} show={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} />
        {isRefreshing && (
          <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-60 z-30">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500"></div>
              <p className="text-white mt-2">Refreshing job detail...</p>
            </div>
          </div>
        )}
        <div className="flex justify-between items-center px-4 md:px-6 py-5 max-sm:pl-12 border-b"
          style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}
        >
          <div className="flex items-center gap-3">
            <IconButton
              onClick={() => navigate("/jobs")}
              aria-label="Go back"
              sx={{
                color: theme.palette.text.main,
                padding: "0px",
                borderRadius: "4px",
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <h1 className="text-2xl font-semibold truncate max-w-[150px] md:max-w-[400px] overflow-hidden capitalize text-ellipsis whitespace-nowrap"
              title={job?.title || job?.ats_title || "Job Title"}
            >
              {job ? (job.title || job.ats_title) : "Loading"}
            </h1>
          </div>

          <ResponsiveActions
            atsName={atsName}
            handleRefresh={handleRefresh}
            handleMarkAsArchived={handleMarkAsArchived}
            toggleModal={toggleModal}
            handleModalOpen={handleModalOpen}
            onSchedule={() => setIsScheduleOpen(true)}
            isMarkingArchived={isMarkingArchived}
            isArchived={isArchived}
          />

        </div>
        {loading ? (
          <div className="mx-auto px-6 sm:px-12 lg:px-16 text-center mt-12">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 border-t-4 border-black rounded-full"></div>
            <p>Loading job details...</p>
          </div>
        ) : (
          <div className="flex flex-col w-full items-start" style={{ height: "calc(100dvh - 72px)" }}>

            <div className="w-full flex py-1 md:py-2 px-4 sm:px-6 border-b gap-8 shadow-md"
              style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.text.main, borderColor: theme.palette.secondary.v3 }}
            >
              <div className="flex gap-2 lg:gap-5 items-center max-lg:flex-col">
                <h2 className="text-sm lg:text-base max-lg:text-center font-semibold lg:whitespace-nowrap">Mandatory Skills</h2>
                <SkillChipsPopover
                  setMobileVisibleSkills={true}
                  width="100%"
                  skills={job.mandatory_skills}
                  emptyLabel="NIL"
                />
              </div>
              <div className="flex gap-2 lg:gap-5 items-center max-lg:flex-col">
                <h2 className="text-sm lg:text-base max-lg:text-center font-semibold lg:whitespace-nowrap">Preferred Skills</h2>
                <SkillChipsPopover
                  setMobileVisibleSkills={true}
                  width="100%"
                  skills={job.preferred_skills}
                  emptyLabel="NIL"
                />
              </div>
              <div className="flex gap-2 lg:gap-5 items-center max-lg:text-center max-lg:flex-col">
                <h3 className="text-sm lg:text-base font-semibold lg:whitespace-nowrap">Minimum Experience</h3>
                <p className="text-sm lg:text-base px-2 rounded-[50px] inline-block border text-center whitespace-nowrap"
                  style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.text.main, borderColor: theme.palette.secondary.v3 }}
                >
                  {job.jd_min_exp} Years
                </p>
              </div>
            </div>

            <div className="h-full w-full p-4 md:px-8 md:pt-8 pb-0 overflow-hidden">
              <CandidatesPage
                key={scheduleVersion}
                jobId={jobId}
                isArchive={isArchived}
                jobExperience={jobExperience}
              />
            </div>
          </div>
        )}
      </div>
      {
        isModalOpen &&
        <AddJobModal
          jobDetails={job}
          jd_uuid={jobUUID}
          setIsAddJobOpen={setIsModalOpen}
          isAddJobOpen={isModalOpen}
          jobDescriptionMain={jobDescription}
          jobTitleMain={jobTitle}
          skillsMain={skills}
          jobExperienceMain={jobExperience}
          mandatorySkillsMain={mandatorySkills}
          preferredSkillsMain={preferredSkills}
          aliasSkillsMain={aliasSkills}
          atsName={atsName}
          isEdit={true}
          atsId={atsId}
        />
      }
      <ScheduleInterviewModal
        isOpen={isScheduleOpen}
        defaultJdUuid={job?.jd_uuid || jobUUID || jobId}
        onClose={() => setIsScheduleOpen(false)}
        onScheduled={() => {
          setIsScheduleOpen(false);
          setScheduleVersion((version) => version + 1);
        }}
      />
      {
        isJdModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-start justify-center z-50">
            <div className="rounded-lg shadow-xl w-full max-w-47rem max-h-[70dvh] mt-[70px] overflow-hidden flex flex-col"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.text.main,
                borderColor: theme.palette.secondary.v3
              }}
              ref={viewJdRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              <div className="flex justify-between items-center px-5 py-4 border-b" style={{ borderColor: theme.palette.secondary.v4 }}>
                <h3 className="font-semibold">Description</h3>
                <button
                  onClick={toggleModal}
                  aria-label="Close description"
                  className="bg-black text-white rounded-full w-6 h-6 flex items-center justify-center"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="text-justify mx-5 sm:mx-14 my-9 overflow-y-auto z-40 always-not-show-scrollbar-horizontal">
                <div
                  className="leading-relaxed p-1"
                  dangerouslySetInnerHTML={{ __html: job.job_description.replace(/\n/g, "<br/>") }}
                />
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default JobDetailPage;
